import React, { useEffect, useState } from 'react'

import LoadingV2 from '@src/components/icons/LoadingV2'

interface SearchingIndicatorProps {
    searchProgress: {
        completed: number
        total: number
    }
}

export const SearchingIndicator: React.FC<SearchingIndicatorProps> = ({ searchProgress }) => {
    // Keep a smooth animated progress value that doesn't jump suddenly
    const [smoothProgress, setSmoothProgress] = useState(0)

    // Calculate raw percentage
    const rawPercentage =
        searchProgress.total > 0 ? Math.round((searchProgress.completed / searchProgress.total) * 100) : 0

    // Use effect to smoothly animate progress changes
    useEffect(() => {
        // Only update if we're not already at the target value
        if (rawPercentage !== smoothProgress) {
            // If progress is reset to 0, immediately reset smooth progress
            if (rawPercentage === 0 && searchProgress.total === 0) {
                setSmoothProgress(0)
                return
            }

            // Otherwise gradually increase the progress
            const timer = setTimeout(() => {
                // When progress is increasing, add 1-3% at a time
                // When progress resets, jump back faster
                if (rawPercentage > smoothProgress) {
                    setSmoothProgress(prev => Math.min(prev + 2, rawPercentage))
                } else {
                    setSmoothProgress(rawPercentage)
                }
            }, 50)

            return () => clearTimeout(timer)
        }
    }, [rawPercentage, smoothProgress, searchProgress.total])

    // Display percentage - use smooth progress for visual display
    const displayPercentage = Math.min(99, smoothProgress)

    return (
        <div className='flex h-full flex-col items-center justify-center px-6 py-8 text-center'>
            {/* Large centered search icon in dark background */}
            <div className='mb-4 flex h-[44px] w-[44px] items-center justify-center rounded-[10px] bg-[#1C1D1F] p-3'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                        d='M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                        stroke='white'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                </svg>
            </div>

            {/* Text and progress bar container */}
            <div className='flex flex-col gap-2'>
                {/* Text */}
                <div className='font-geist text-xs font-medium leading-4 text-mach-sub-text'>
                    Searching all blockchains
                </div>

                {/* Progress bar section */}
                <div className='flex w-[280px] items-center gap-2'>
                    <LoadingV2 className='h-5 w-5 flex-shrink-0 animate-spin' />
                    <div className='relative h-2 w-[200px] flex-shrink-0 overflow-hidden rounded-full bg-[#1C1D1F]'>
                        <div
                            className='absolute left-0 top-0 h-full rounded-full bg-white transition-all duration-300 ease-out'
                            style={{ width: `${displayPercentage}%` }}
                        />
                    </div>
                    <div className='w-[40px] flex-shrink-0 text-xs font-medium leading-4 text-white'>
                        {displayPercentage}%
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SearchingIndicator
